import NiceModal from '@ebay/nice-modal-react';
import { DropdownSelectionProps } from '@shared/components/DropdownButton/DropdownSelection';
import Tooltip from '@shared/components/Tooltip/Tooltip';
import cx from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SCOPES, formatApiKeyDisplay, formatExpiryCountdown } from 'api/meedioBackend/apiKeys/constants';
import { ApiKeyItem } from 'api/meedioBackend/apiKeys/types';
import { ReactComponent as Clock } from 'assets/icons/Clock.svg';
import { ReactComponent as Delete } from 'assets/icons/Delete.svg';
import { ReactComponent as Refresh } from 'assets/icons/Refresh.svg';
import { ReactComponent as ShieldLock } from 'assets/icons/ShieldLock.svg';
import OptionsDropdown from 'components/OptionsDropdown/OptionsDropdown';
import useToast from 'contexts/ToastProvider/useToast';
import testingConstants from 'utils/testingConstants';

import RotateApiKeyModal from '../modals/RotateApiKeyModal';
import showRevokeApiKeyConfirm from '../modals/showRevokeApiKeyConfirm';

export type ApiKeyRowVariant = 'active' | 'expired';

interface ApiKeyRowProps {
  item: ApiKeyItem;
  variant: ApiKeyRowVariant;
  /** Backend workspace UUID the key belongs to; `null` until resolved (menu actions no-op). */
  workspaceUuid: string | null;
  /** Re-fetches the Active and Expired lists after a rotate/revoke mutation. */
  onKeysChanged: () => void;
}

const COUNTDOWN_REFRESH_MS = 60_000;
const tagBaseClass = 'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs leading-normal';

const ApiKeyRow = ({ item, variant, workspaceUuid, onKeysChanged }: ApiKeyRowProps) => {
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const displayString = formatApiKeyDisplay(item.prefix, item.hint);

  const { expires_at: expiresAt } = item;
  const [countdown, setCountdown] = useState(() => (expiresAt ? formatExpiryCountdown(expiresAt) : null));

  const scopeLabels = item.scopes
    .map((scope) => SCOPES.find((definition) => definition.scope === scope)?.labelKey)
    .filter((labelKey): labelKey is string => Boolean(labelKey))
    .map((labelKey) => t(labelKey));

  const statusLabel =
    item.status === 'revoked'
      ? t('workspaces_settings.api_keys.status.revoked')
      : t('workspaces_settings.api_keys.status.expired');

  const handleRotate = () => {
    if (!workspaceUuid) return;
    void NiceModal.show(RotateApiKeyModal, {
      workspaceUuid,
      keyId: item.id,
      currentExpiresAt: item.expires_at,
      onRotated: onKeysChanged,
    });
  };

  const handleRevoke = () => {
    if (!workspaceUuid) return;
    showRevokeApiKeyConfirm({ workspaceUuid, keyId: item.id, onRevoked: onKeysChanged, pushToast });
  };

  // Active rows expose Rotate + Revoke; expired rows are read-only.
  const menuOptions: DropdownSelectionProps[] = [
    {
      title: t('workspaces_settings.api_keys.menu.rotate'),
      icon: <Refresh className="text-gray-80 stroke-1.5 mr-2 h-5 w-5 shrink-0 stroke-current" />,
      onClick: handleRotate,
      hasSelectedCheck: false,
      dataTestId: testingConstants.apiKeyRowMenuRotate,
    },
    {
      title: t('workspaces_settings.api_keys.menu.revoke'),
      icon: <Delete className="text-tertiary-50 stroke-1.5 mr-2 h-5 w-5 shrink-0 stroke-current" />,
      onClick: handleRevoke,
      hasSelectedCheck: false,
      className: 'text-tertiary-50',
      dataTestId: testingConstants.apiKeyRowMenuRevoke,
    },
  ];

  useEffect(() => {
    if (variant !== 'active' || !expiresAt) return;

    setCountdown(formatExpiryCountdown(expiresAt));
    const interval = setInterval(() => setCountdown(formatExpiryCountdown(expiresAt)), COUNTDOWN_REFRESH_MS);
    return () => clearInterval(interval);
  }, [variant, expiresAt]);

  return (
    <div className="flex items-center justify-between gap-2 py-3" data-testid={`apiKeyRow-${item.id}`}>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-grayscale-black truncate text-sm font-medium">{item.name}</span>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-gray-80 text-size-sm truncate">{displayString}</span>
          <Tooltip
            label={t('workspaces_settings.api_keys.scope_tooltip_header')}
            description={scopeLabels.join(', ')}
            placement="top"
          >
            <span className={cx(tagBaseClass, 'bg-gray-30 text-gray-80')}>
              <ShieldLock className="h-3.5 w-3.5 shrink-0 stroke-current stroke-1.5" />
              {t('workspaces_settings.api_keys.scope_count', { count: item.scopes.length })}
            </span>
          </Tooltip>
          {variant === 'active' ? (
            <span className={cx(tagBaseClass, 'bg-primaryComp-10 text-primary-70')}>
              {expiresAt ? (
                <>
                  <Clock className="h-3.5 w-3.5 shrink-0 stroke-current stroke-1.5" />
                  {countdown}
                </>
              ) : (
                t('workspaces_settings.api_keys.no_expiration')
              )}
            </span>
          ) : (
            <span className={cx(tagBaseClass, 'bg-tertiary-10 text-tertiary-70')}>{statusLabel}</span>
          )}
        </div>
      </div>
      {variant === 'active' && (
        <OptionsDropdown
          options={menuOptions}
          placement="bottom-end"
          openInPortal
          dropdownContainerClass="!min-w-[220px]"
        />
      )}
    </div>
  );
};

export default ApiKeyRow;
